import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import qs from 'query-string';
import { useFetch } from 'app/utils/hooks';
import { apiUrls } from 'app/api.config';
import {
  ShareResponse,
  ShareServiceResponse,
  ShareType,
} from 'app/components/Shares/types';

import { routes } from 'app/routing/routesConfig';
import { Share404 } from 'app/components/Shares/Share404';
import {
  formatFetchUrl,
  formatFetchUrlV2,
  getShareItem,
  getShareLink,
  getShareLinkV2,
} from 'app/components/Shares/utils';
import { ChakraAppLoader } from 'app/components/Core';
import { useAppDispatch } from 'app/store';
import {
  setSharedAudio,
  setSharedPlaylist,
  setShareId,
} from 'app/store/Shares/slice';
import { AudiosShare } from 'app/components/Shares/AudiosShare';
import { SharedPlaylistResponse } from 'types/shares.types';
import { isArray } from 'lodash';
import { InitializationLayer } from '../InitializationLayer';

interface SharesComponentProps {
  shareToken?: string | null;
  getPublicShareLink: (data: ShareServiceResponse | null) => string | undefined;
  formatFetchShareUrl: (link: string, token: string) => string;
}

const SharesComponent: React.FC<SharesComponentProps> = ({
  shareToken,
  getPublicShareLink,
  formatFetchShareUrl,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const fetchShareService = useFetch<{}, ShareServiceResponse>();
  const fetchShare = useFetch<{}, ShareResponse | SharedPlaylistResponse>();
  const publicShareLink = getPublicShareLink(fetchShareService.data);
  const shareItem = getShareItem(fetchShare.data);
  const loading = !fetchShareService.done || !fetchShare.done;
  const unknownShareEntity =
    shareItem &&
    ![ShareType.PLAYLIST, ShareType.AUDIO].some(
      (type) => type === shareItem.type
    );

  const discoverShareService = async () => {
    try {
      await fetchShareService.callAPI(apiUrls.services.shares);
    } catch (e) {
      navigate(routes.webPageNotFound.path);
    }
  };

  const handleFetchShare = async (link: string, token: string) => {
    try {
      await fetchShare.callAPI(formatFetchShareUrl(link, token));
    } catch (e) {
      // proceed
    }
  };

  useEffect(() => {
    discoverShareService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shareToken) {
      const shareId = shareToken.toString().split('.')[0];
      dispatch(setShareId(shareId));
    } else {
      dispatch(setShareId(undefined));
    }
    return () => {
      dispatch(setShareId(undefined));
    };
  }, [dispatch, shareToken]);

  useEffect(() => {
    if (shareToken && publicShareLink) {
      handleFetchShare(publicShareLink, shareToken.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken, publicShareLink]);

  if (
    !shareToken ||
    (!loading && fetchShare.error) ||
    (!loading && unknownShareEntity)
  ) {
    return <Share404 />;
  }

  if (fetchShare.data && !loading) {
    const shareItem = getShareItem(fetchShare.data);

    if (shareItem?.type === ShareType.PLAYLIST) {
      dispatch(setSharedPlaylist(fetchShare.data as SharedPlaylistResponse));
    }

    if (shareItem?.type === ShareType.AUDIO) {
      dispatch(setSharedAudio(fetchShare.data as ShareResponse));
    }

    return (
      <InitializationLayer>
        <AudiosShare shareResponse={fetchShare.data} />
      </InitializationLayer>
    );
  }

  return <ChakraAppLoader />;
};

export const SharesOld = () => {
  const { search } = useLocation();
  const { shareToken } = qs.parse(search, { decode: false }) || {};

  return (
    <SharesComponent
      shareToken={isArray(shareToken) ? shareToken[0] : shareToken}
      getPublicShareLink={getShareLink}
      formatFetchShareUrl={formatFetchUrl}
    />
  );
};

export const Shares = () => {
  const { shareToken } = useParams();
  return (
    <SharesComponent
      shareToken={shareToken}
      getPublicShareLink={getShareLinkV2}
      formatFetchShareUrl={formatFetchUrlV2}
    />
  );
};
